import type { AdaptationSuggestion } from '../dto/AdaptationSuggestion';
import type { DailyInspiration } from '../dto/DailyInspiration';
import type { DailyRoutineDetails, RecentDailyRoutine } from '../dto/DailyRoutineDetails';
import type { DatabaseInitializationStatus, RuntimeDiagnostic } from '../dto/RuntimeDiagnostic';
import type { WorkoutSessionDetails } from '../dto/WorkoutSessionDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { LocalDatabase } from '../ports/LocalDatabase';
import type { RuntimeCapabilityProvider } from '../ports/RuntimeCapabilityProvider';
import type { ExportEnvelope } from '../../domain/backup/ExportEnvelope';
import type { UserChoice } from '../../domain/adaptive-guidance/enums';
import type { EmotionalState } from '../../domain/emotion/enums';
import type { DayMode } from '../../domain/routine/enums';
import type { DailyStepId, ExerciseLogId, ProfileId } from '../../domain/shared/ids';
import { failure, type Result } from '../../domain/shared/Result';
import type { ChangeDailyMode } from '../use-cases/ChangeDailyMode';
import type { CompleteDailyStep } from '../use-cases/CompleteDailyStep';
import type { DeleteAllProfileData } from '../use-cases/DeleteAllProfileData';
import type { ExportProfileData } from '../use-cases/ExportProfileData';
import type { GetAdaptationSuggestion } from '../use-cases/GetAdaptationSuggestion';
import type { GetDailyRoutine } from '../use-cases/GetDailyRoutine';
import type { GetOrCreateTodayRoutine } from '../use-cases/GetOrCreateTodayRoutine';
import type { GetOrCreateTodayWorkout } from '../use-cases/GetOrCreateTodayWorkout';
import type { GetTodayInspiration } from '../use-cases/GetTodayInspiration';
import type { ImportFilePreview, PreviewImportFile } from '../use-cases/PreviewImportFile';
import type { ImportProfileData, ImportProfileDataResult } from '../use-cases/ImportProfileData';
import type {
  DefaultProfileConfiguration,
  InitializeLocalPersistence,
} from '../use-cases/InitializeLocalPersistence';
import type { ListRecentDailyRoutines } from '../use-cases/ListRecentDailyRoutines';
import type { PostponeDailyStep } from '../use-cases/PostponeDailyStep';
import type { ReduceWorkoutSession } from '../use-cases/ReduceWorkoutSession';
import type { RespondToAdaptationSuggestion } from '../use-cases/RespondToAdaptationSuggestion';
import type { ResetDailyStep } from '../use-cases/ResetDailyStep';
import type { ReorderDailySteps, ReorderScope } from '../use-cases/ReorderDailySteps';
import type { SeedMvpRoutine } from '../use-cases/SeedMvpRoutine';
import type { SetEmotionalState } from '../use-cases/SetEmotionalState';
import type { SeedPhilosophyCorpus } from '../use-cases/SeedPhilosophyCorpus';
import type { SkipDailyStep } from '../use-cases/SkipDailyStep';
import type { StartDailyStep } from '../use-cases/StartDailyStep';
import type { UpdateExerciseStatus } from '../use-cases/UpdateExerciseStatus';

export interface DailyMissionsClient {
  readonly getRuntimeDiagnostic: () => RuntimeDiagnostic;
  readonly getToday: () => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly getByDate: (logicalDate: string) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly listRecent: () => Promise<Result<readonly RecentDailyRoutine[], RoutineError>>;
  readonly start: (
    logicalDate: string,
    stepId: DailyStepId,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly complete: (
    logicalDate: string,
    stepId: DailyStepId,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly reset: (
    logicalDate: string,
    stepId: DailyStepId,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly skip: (
    logicalDate: string,
    stepId: DailyStepId,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly postpone: (
    logicalDate: string,
    stepId: DailyStepId,
    targetDate?: string,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly reorder: (
    logicalDate: string,
    orderedIds: readonly DailyStepId[],
    scope: ReorderScope,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly changeMode: (
    logicalDate: string,
    mode: DayMode,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly setEmotionalState: (
    logicalDate: string,
    state: EmotionalState | null,
  ) => Promise<Result<DailyRoutineDetails, RoutineError>>;
  readonly getWorkout: (
    dayMode: DayMode,
  ) => Promise<Result<WorkoutSessionDetails, RoutineError>>;
  readonly completeExercise: (
    logicalDate: string,
    exerciseId: ExerciseLogId,
    options?: { readonly userWeight?: number; readonly weightUnit?: 'kg' | 'lb' },
  ) => Promise<Result<WorkoutSessionDetails, RoutineError>>;
  readonly skipExercise: (
    logicalDate: string,
    exerciseId: ExerciseLogId,
  ) => Promise<Result<WorkoutSessionDetails, RoutineError>>;
  readonly resetExercise: (
    logicalDate: string,
    exerciseId: ExerciseLogId,
  ) => Promise<Result<WorkoutSessionDetails, RoutineError>>;
  readonly replaceExercise: (
    logicalDate: string,
    exerciseId: ExerciseLogId,
  ) => Promise<Result<WorkoutSessionDetails, RoutineError>>;
  readonly reduceWorkout: (
    logicalDate: string,
  ) => Promise<Result<WorkoutSessionDetails, RoutineError>>;
  readonly getInspiration: () => Promise<Result<DailyInspiration, RoutineError>>;
  readonly getAdaptationSuggestion: (
    logicalDate: string,
  ) => Promise<Result<AdaptationSuggestion, RoutineError>>;
  readonly respondToAdaptationSuggestion: (
    logicalDate: string,
    choice: UserChoice,
  ) => Promise<Result<AdaptationSuggestion, RoutineError>>;
  readonly exportData: () => Promise<Result<ExportEnvelope, RoutineError>>;
  readonly previewImport: (rawJson: string) => Result<ImportFilePreview, RoutineError>;
  readonly confirmImport: (
    envelope: ExportEnvelope,
  ) => Promise<Result<ImportProfileDataResult, RoutineError>>;
  readonly deleteAllData: () => Promise<Result<void, RoutineError>>;
}

interface DailyMissionsUseCases {
  readonly initialize: InitializeLocalPersistence;
  readonly seed: SeedMvpRoutine;
  readonly seedPhilosophy: SeedPhilosophyCorpus;
  readonly getToday: GetOrCreateTodayRoutine;
  readonly getByDate: GetDailyRoutine;
  readonly listRecent: ListRecentDailyRoutines;
  readonly start: StartDailyStep;
  readonly complete: CompleteDailyStep;
  readonly reset: ResetDailyStep;
  readonly skip: SkipDailyStep;
  readonly postpone: PostponeDailyStep;
  readonly reorder: ReorderDailySteps;
  readonly changeMode: ChangeDailyMode;
  readonly setEmotionalState: SetEmotionalState;
  readonly getWorkout: GetOrCreateTodayWorkout;
  readonly updateExercise: UpdateExerciseStatus;
  readonly reduceWorkout: ReduceWorkoutSession;
  readonly getInspiration: GetTodayInspiration;
  readonly getAdaptationSuggestion: GetAdaptationSuggestion;
  readonly respondToAdaptationSuggestion: RespondToAdaptationSuggestion;
  readonly exportData: ExportProfileData;
  readonly previewImport: PreviewImportFile;
  readonly confirmImport: ImportProfileData;
  readonly deleteAllData: DeleteAllProfileData;
}

export class DailyMissionsService implements DailyMissionsClient {
  #profileId: ProfileId | undefined;
  #startup: Promise<Result<ProfileId, RoutineError>> | undefined;
  #databaseInitializationStatus: DatabaseInitializationStatus = 'not_started';

  public constructor(
    private readonly useCases: DailyMissionsUseCases,
    private readonly profile: DefaultProfileConfiguration,
    private readonly database: LocalDatabase,
    private readonly runtimeCapabilities: RuntimeCapabilityProvider,
  ) {}

  private ensureStarted(): Promise<Result<ProfileId, RoutineError>> {
    if (!this.#startup) {
      this.#databaseInitializationStatus = 'initializing';
      const startup = this.startup().then((result) => {
        this.#databaseInitializationStatus = result.ok ? 'ready' : 'failed';
        if (!result.ok) this.#startup = undefined;
        return result;
      });
      this.#startup = startup;
      return startup;
    }
    return this.#startup;
  }

  private invalidateStartup(): void {
    this.database.close();
    this.#profileId = undefined;
    this.#startup = undefined;
    this.#databaseInitializationStatus = 'failed';
  }

  private async startup(): Promise<Result<ProfileId, RoutineError>> {
    try {
      const initialized = await this.useCases.initialize.execute(this.profile);
      if (!initialized.ok) return failure(toRoutineError(initialized.error));
      this.#profileId = initialized.value.profileId;
      await this.useCases.seed.execute(this.#profileId);
      await this.useCases.seedPhilosophy.execute();
      return { ok: true, value: this.#profileId };
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }

  private async withProfile<Value>(
    action: (profileId: ProfileId) => Promise<Result<Value, RoutineError>>,
  ): Promise<Result<Value, RoutineError>> {
    const started = await this.ensureStarted();
    if (!started.ok) return started;
    try {
      return await action(started.value);
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }

  public getRuntimeDiagnostic(): RuntimeDiagnostic {
    return {
      ...this.runtimeCapabilities.getCapabilities(),
      databaseInitializationStatus: this.#databaseInitializationStatus,
    };
  }

  public async getToday(): Promise<Result<DailyRoutineDetails, RoutineError>> {
    const result = await this.withProfile((profileId) => this.useCases.getToday.execute(profileId));
    if (!result.ok) this.invalidateStartup();
    return result;
  }

  public getByDate(logicalDate: string): Promise<Result<DailyRoutineDetails, RoutineError>> {
    return this.withProfile((profileId) => this.useCases.getByDate.execute(profileId, logicalDate));
  }

  public listRecent(): Promise<Result<readonly RecentDailyRoutine[], RoutineError>> {
    return this.withProfile((profileId) => this.useCases.listRecent.execute(profileId));
  }

  public start(logicalDate: string, stepId: DailyStepId) {
    return this.withProfile((profileId) =>
      this.useCases.start.execute(profileId, logicalDate, stepId),
    );
  }

  public complete(logicalDate: string, stepId: DailyStepId) {
    return this.withProfile((profileId) =>
      this.useCases.complete.execute(profileId, logicalDate, stepId),
    );
  }

  public reset(logicalDate: string, stepId: DailyStepId) {
    return this.withProfile((profileId) =>
      this.useCases.reset.execute(profileId, logicalDate, stepId),
    );
  }

  public skip(logicalDate: string, stepId: DailyStepId) {
    return this.withProfile((profileId) =>
      this.useCases.skip.execute(profileId, logicalDate, stepId),
    );
  }

  public postpone(logicalDate: string, stepId: DailyStepId, targetDate?: string) {
    return this.withProfile((profileId) =>
      this.useCases.postpone.execute(profileId, logicalDate, stepId, targetDate),
    );
  }

  public reorder(logicalDate: string, orderedIds: readonly DailyStepId[], scope: ReorderScope) {
    return this.withProfile((profileId) =>
      this.useCases.reorder.execute(profileId, logicalDate, orderedIds, scope),
    );
  }

  public changeMode(logicalDate: string, mode: DayMode) {
    return this.withProfile((profileId) =>
      this.useCases.changeMode.execute(profileId, logicalDate, mode),
    );
  }

  public setEmotionalState(logicalDate: string, state: EmotionalState | null) {
    return this.withProfile((profileId) =>
      this.useCases.setEmotionalState.execute(profileId, logicalDate, state),
    );
  }

  public getWorkout(dayMode: DayMode) {
    return this.withProfile((profileId) =>
      this.useCases.getWorkout.execute(profileId, dayMode),
    );
  }

  public completeExercise(
    logicalDate: string,
    exerciseId: ExerciseLogId,
    options?: { readonly userWeight?: number; readonly weightUnit?: 'kg' | 'lb' },
  ) {
    return this.withProfile((profileId) =>
      this.useCases.updateExercise.execute(profileId, logicalDate, exerciseId, 'complete', options),
    );
  }

  public skipExercise(logicalDate: string, exerciseId: ExerciseLogId) {
    return this.withProfile((profileId) =>
      this.useCases.updateExercise.execute(profileId, logicalDate, exerciseId, 'skip'),
    );
  }

  public resetExercise(logicalDate: string, exerciseId: ExerciseLogId) {
    return this.withProfile((profileId) =>
      this.useCases.updateExercise.execute(profileId, logicalDate, exerciseId, 'reset'),
    );
  }

  public replaceExercise(logicalDate: string, exerciseId: ExerciseLogId) {
    return this.withProfile((profileId) =>
      this.useCases.updateExercise.replace(profileId, logicalDate, exerciseId),
    );
  }

  public reduceWorkout(logicalDate: string) {
    return this.withProfile((profileId) =>
      this.useCases.reduceWorkout.execute(profileId, logicalDate),
    );
  }

  public getInspiration() {
    return this.withProfile(() => this.useCases.getInspiration.execute());
  }

  public getAdaptationSuggestion(logicalDate: string) {
    return this.withProfile((profileId) =>
      this.useCases.getAdaptationSuggestion.execute(profileId, logicalDate),
    );
  }

  public respondToAdaptationSuggestion(logicalDate: string, choice: UserChoice) {
    return this.withProfile((profileId) =>
      this.useCases.respondToAdaptationSuggestion.execute(profileId, logicalDate, choice),
    );
  }

  public exportData() {
    return this.withProfile((profileId) => this.useCases.exportData.execute(profileId));
  }

  public previewImport(rawJson: string): Result<ImportFilePreview, RoutineError> {
    return this.useCases.previewImport.execute(rawJson);
  }

  public confirmImport(envelope: ExportEnvelope) {
    return this.withProfile((profileId) => this.useCases.confirmImport.execute(profileId, envelope));
  }

  public async deleteAllData(): Promise<Result<void, RoutineError>> {
    const result = await this.withProfile((profileId) => this.useCases.deleteAllData.execute(profileId));
    if (result.ok) this.invalidateStartup();
    return result;
  }
}
